import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import '../../styles/global.css';

interface SearchResult {
  item_id: number;
  material: string | null;
  srednica: string | null;
  waga: string | null;
  szpula: string | null;
  kolor: string | null;
  barwnik: string | null;
  doz_procent: string | null;
  marka: string | null;
  nazwa_oryg: string | null;
  indeks_kat: string | null;
  do_produkcji: string | null;
  zmiana_1: string | null;
  zmiana_2: string | null;
  uwagi: string | null;
  line_id: number;
  line_name: string;
  week: string;
  assignment_id: number;
  status: string;
  notes: string | null;
  wykonano_ilosc: string | null;
  spool_type: 'small' | 'big' | null;
  /** 1 when at least one spool still waits at the rewinding station */
  has_pending_rewind: number | boolean;
  /** 1 when at least one spool of this item was already rewound */
  has_done_rewind: number | boolean;
  /**
   * Status as the lines show it, computed by the backend (the status filter uses the same value):
   * an item at the rewinding station is "rewinding" although the pivot says "in_progress".
   */
  effective_status: string;
}

interface ProductionLine {
  id: number;
  name: string;
}

/** Row palette shared by the table and the legend below it. */
const ROW_COLORS = {
  completed: '#d1fae5',
  rewinding: '#cfe2ff',
  inProgress: '#fef3c7',
  problem: '#f8d7da',
} as const;

const AdminSearch: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  const [lineFilter, setLineFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [weekFilter, setWeekFilter] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [availableWeeks, setAvailableWeeks] = useState<string[]>([]);
  const [availableLines, setAvailableLines] = useState<ProductionLine[]>([]);
  const [viewingNote, setViewingNote] = useState<{ notes: string; indeks_kat: string; line_name: string } | null>(null);

  useEffect(() => {
    // Load available lines and weeks on mount
    api.get('/production-lines').then((res) => {
      setAvailableLines(res.data);
    }).catch(() => {});
  }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setHasSearched(true);

    try {
      const params = new URLSearchParams();
      params.set('q', searchQuery.trim());
      if (lineFilter) params.set('line_id', lineFilter);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (weekFilter) params.set('week', weekFilter);

      const res = await api.get(`/admin/search?${params.toString()}`);
      setResults(res.data.results);
      setTotal(res.data.total);
      if (res.data.available_weeks) setAvailableWeeks(res.data.available_weeks);
      if (res.data.available_lines) setAvailableLines(res.data.available_lines);
    } catch (err) {
      console.error('Search error:', err);
      setResults([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'pending': return t('adminSearch.statusPending');
      case 'in_progress': return t('adminSearch.statusInProgress');
      case 'completed': return t('adminSearch.statusCompleted');
      case 'rewinding': return t('adminSearch.statusRewinding');
      case 'problem_bb': return 'Problem BB';
      case 'problem_awaria': return 'Problem Awaria';
      case 'niepelna_produkcja': return t('adminSearch.statusIncomplete');
      default: return status;
    }
  };

  const getStatusBadgeStyle = (status: string): React.CSSProperties => {
    switch (status) {
      case 'pending':
        return { backgroundColor: '#e2e8f0', color: '#4a5568', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 };
      case 'in_progress':
        return { backgroundColor: '#fef3c7', color: '#92400e', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 };
      case 'completed':
        return { backgroundColor: '#d1fae5', color: '#065f46', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 };
      case 'rewinding':
        return { backgroundColor: '#cfe2ff', color: '#1e40af', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 };
      case 'problem_bb':
      case 'problem_awaria':
        return { backgroundColor: '#f8d7da', color: '#721c24', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 };
      case 'niepelna_produkcja':
        return { backgroundColor: '#fff3cd', color: '#856404', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 };
      default:
        return { backgroundColor: '#e2e8f0', color: '#4a5568', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 };
    }
  };

  /**
   * Row colour, mirroring getRowColor() in MyProduction so that the search shows the same
   * state as the line panel. Precedence (identical to the lines):
   *   green  = fully rewound, or closed, or fully produced on a small-spool line
   *   blue   = waiting at the rewinding station
   *   red    = failure, or an open note on a position that is not produced yet
   *   yellow = in progress / incomplete production
   * A big-spool line does NOT turn green just from reaching the plan — it waits for rewinding.
   */
  const getRowColor = (r: SearchResult): string => {
    const pendingRewind = !!Number(r.has_pending_rewind);
    const doneRewind = !!Number(r.has_done_rewind);
    const planned = r.do_produkcji ? parseInt(r.do_produkcji) : 0;
    const produced = (r.zmiana_1 ? parseInt(r.zmiana_1) : 0) + (r.zmiana_2 ? parseInt(r.zmiana_2) : 0);
    const hasNotes = !!(r.notes || r.uwagi);

    // Everything rewound → the position is done.
    if (doneRewind && !pendingRewind) return ROW_COLORS.completed;
    // Still at the rewinding station — takes precedence over "fully produced".
    if (pendingRewind) return ROW_COLORS.rewinding;

    if (r.status === 'problem_awaria') return ROW_COLORS.problem;
    // "Brak barwnika" highlights only the dye cell on the lines — the row stays white there,
    // so it stays white here too.
    if (r.status === 'problem_bb') return 'white';

    if (r.status === 'completed') return ROW_COLORS.completed;

    if (hasNotes && produced < planned) return ROW_COLORS.problem;

    // Small-spool lines are finished the moment the plan is reached; big-spool ones are not.
    if (r.spool_type !== 'big' && planned > 0 && produced >= planned) return ROW_COLORS.completed;

    if (r.status === 'in_progress' || r.status === 'niepelna_produkcja') return ROW_COLORS.inProgress;

    return 'white';
  };

  return (
    <div>
      <h2 style={{ marginBottom: '20px', color: '#1a202c' }}>{t('adminSearch.title')}</h2>

      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 300px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#4a5568' }}>
            {t('adminSearch.searchLabel')}
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('adminSearch.searchPlaceholder')}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '2px solid #e1e8ed',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#4a5568',
              outline: 'none',
            }}
          />
        </div>

        <div style={{ flex: '0 0 180px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#4a5568' }}>
            {t('adminSearch.lineLabel')}
          </label>
          <select
            value={lineFilter}
            onChange={(e) => setLineFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '2px solid #e1e8ed',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#4a5568',
              outline: 'none',
              backgroundColor: 'white',
            }}
          >
            <option value="">{t('adminSearch.allLines')}</option>
            {availableLines.map((line) => (
              <option key={line.id} value={line.id}>{line.name}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: '0 0 200px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#4a5568' }}>
            {t('adminSearch.statusLabel')}
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '2px solid #e1e8ed',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#4a5568',
              outline: 'none',
              backgroundColor: 'white',
            }}
          >
            <option value="all">{t('adminSearch.allStatuses')}</option>
            <option value="pending">{t('adminSearch.statusPending')}</option>
            <option value="in_progress">{t('adminSearch.statusInProgress')}</option>
            <option value="completed">{t('adminSearch.statusCompleted')}</option>
            <option value="rewinding">{t('adminSearch.statusRewinding')}</option>
            <option value="problem_bb">Problem BB</option>
            <option value="problem_awaria">Problem Awaria</option>
            <option value="niepelna_produkcja">{t('adminSearch.statusIncomplete')}</option>
          </select>
        </div>

        <div style={{ flex: '0 0 150px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, marginBottom: '4px', color: '#4a5568' }}>
            {t('adminSearch.weekLabel')}
          </label>
          <select
            value={weekFilter}
            onChange={(e) => setWeekFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 14px',
              border: '2px solid #e1e8ed',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#4a5568',
              outline: 'none',
              backgroundColor: 'white',
            }}
          >
            <option value="">{t('adminSearch.allWeeks')}</option>
            {availableWeeks.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: '0 0 auto' }}>
          <button
            onClick={handleSearch}
            disabled={loading || !searchQuery.trim()}
            style={{
              padding: '10px 24px',
              backgroundColor: '#3182ce',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: loading || !searchQuery.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !searchQuery.trim() ? 0.6 : 1,
            }}
          >
            {loading ? t('common.loading') : t('common.search')}
          </button>
        </div>
      </div>

      {/* Results */}
      {!hasSearched && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#a0aec0' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#128269;</div>
          <p style={{ fontSize: '16px' }}>{t('adminSearch.initialMessage')}</p>
        </div>
      )}

      {hasSearched && !loading && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#a0aec0' }}>
          <p style={{ fontSize: '16px' }}>{t('adminSearch.noResults')}</p>
        </div>
      )}

      {hasSearched && results.length > 0 && (
        <>
          <div style={{ marginBottom: '12px', fontSize: '14px', color: '#718096' }}>
            {t('adminSearch.resultsCount', { count: total.toString() })}
            {total >= 500 && (
              <span style={{ color: '#e53e3e', marginLeft: '8px' }}>
                ({t('adminSearch.limitReached')})
              </span>
            )}
          </div>

          {/* Colour legend — 1:1 with getRowColor() above. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '12px', fontSize: '13px', color: '#4a5568' }}>
            {[
              { color: ROW_COLORS.inProgress, label: t('adminSearch.legendInProgress') },
              { color: ROW_COLORS.rewinding, label: t('adminSearch.legendRewinding') },
              { color: ROW_COLORS.completed, label: t('adminSearch.legendCompleted') },
              { color: ROW_COLORS.problem, label: t('adminSearch.legendProblem') },
            ].map(({ color, label }) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ width: '16px', height: '16px', borderRadius: '4px', border: '1px solid #cbd5e1', backgroundColor: color }} />
                {label}
              </span>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#2d3748', color: 'white' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>{t('adminSearch.colLine')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>{t('adminSearch.colWeek')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>{t('adminSearch.colIndex')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>{t('adminSearch.colName')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', whiteSpace: 'nowrap' }}>{t('adminSearch.colMaterial')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('adminSearch.colToProduced')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('adminSearch.colShift1')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{t('adminSearch.colShift2')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>{t('adminSearch.colStatus')}</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>{t('adminSearch.colNotes')}</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, idx) => (
                  <tr
                    key={`${r.assignment_id}-${r.item_id}-${idx}`}
                    style={{ backgroundColor: getRowColor(r), borderBottom: '1px solid #e2e8f0' }}
                  >
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                      <span
                        onClick={() => navigate(`/admin/production-lines/${r.line_id}/view?week=${encodeURIComponent(r.week)}`)}
                        style={{ color: '#3182ce', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}
                      >
                        {r.line_name}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{r.week}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 600 }}>{r.indeks_kat}</td>
                    <td style={{ padding: '10px 12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.nazwa_oryg}</td>
                    <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{r.material}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.do_produkcji}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.zmiana_1}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.zmiana_2}</td>
                    <td style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <span style={getStatusBadgeStyle(r.effective_status)}>
                        {getStatusLabel(r.effective_status)}
                      </span>
                    </td>
                    <td
                      style={{ padding: '10px 12px', textAlign: 'center', whiteSpace: 'nowrap', cursor: (r.notes || r.uwagi) ? 'pointer' : 'default' }}
                      onClick={() => {
                        const noteText = r.notes || r.uwagi;
                        if (noteText) {
                          setViewingNote({ notes: noteText, indeks_kat: r.indeks_kat || '', line_name: r.line_name });
                        }
                      }}
                    >
                      {(r.notes || r.uwagi) ? (
                        <span style={{ fontSize: '18px' }} title={r.notes || r.uwagi || ''}>&#9888;&#65039;</span>
                      ) : (
                        <span style={{ fontSize: '18px', color: '#9ca3af' }}>&#8212;</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {/* Notes Modal */}
      {viewingNote && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setViewingNote(null)}
        >
          <div
            style={{
              backgroundColor: 'white',
              padding: '30px',
              borderRadius: '12px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0, color: '#667eea' }}>{t('adminSearch.colNotes')}</h3>
            <div style={{ marginBottom: '12px', fontSize: '13px', color: '#718096' }}>
              <strong>{viewingNote.line_name}</strong> &mdash; {viewingNote.indeks_kat}
            </div>
            <div style={{
              padding: '15px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #f59e0b',
              color: '#78350f',
              fontSize: '15px',
              fontWeight: 500,
            }}>
              {viewingNote.notes}
            </div>
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button
                onClick={() => setViewingNote(null)}
                style={{
                  padding: '8px 20px',
                  backgroundColor: '#e2e8f0',
                  color: '#4a5568',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSearch;
